"use strict";
/**
 * Shared AI Workspace Service
 * Used by both ilearn (student) and iteach (teacher) apps.
 *
 * Contains student session methods common to both apps.
 * Faculty config/template methods remain app-specific in iteach.
 *
 * Usage:
 *   import { createAiWorkspaceService } from '@conceptwaves/shared-components';
 *   const workspaceService = createAiWorkspaceService({ baseUrl, getToken });
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.createAiWorkspaceService = void 0;
function buildHeaders(token) {
    return {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
    };
}
async function request(url, init) {
    const response = await fetch(url, init);
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Request failed: ${response.statusText}`);
    }
    return response.json();
}
function createAiWorkspaceService(config) {
    const { baseUrl, getToken } = config;
    const wsBase = `${baseUrl}v1/ai-workspace`;
    const headers = () => buildHeaders(getToken());
    return {
        /** Check if an AI prompt config exists for a study resource */
        async checkConfigExists(resourceId) {
            try {
                const data = await request(`${wsBase}/config/resource/${resourceId}`, { method: 'GET', headers: headers() });
                return data.success === true;
            }
            catch {
                return false;
            }
        },
        /** Start a new workspace session for a study resource */
        async startSession(studyResourceId) {
            try {
                return await request(`${wsBase}/session/start`, {
                    method: 'POST',
                    headers: headers(),
                    body: JSON.stringify({ studyResourceId }),
                });
            }
            catch {
                return { success: false, error: 'Failed to start session' };
            }
        },
        /** Get existing session by study resource ID (for current user) */
        async getSessionByResource(resourceId) {
            try {
                return await request(`${wsBase}/session/resource/${resourceId}`, { method: 'GET', headers: headers() });
            }
            catch {
                return { success: false, error: 'No session found' };
            }
        },
        /** Get session by session ID */
        async getSession(sessionId) {
            try {
                return await request(`${wsBase}/session/${sessionId}`, { method: 'GET', headers: headers() });
            }
            catch {
                return { success: false, error: 'Failed to get session' };
            }
        },
        /** Send a message in an active session */
        async sendMessage(sessionId, message) {
            try {
                return await request(`${wsBase}/session/${sessionId}/message`, {
                    method: 'POST',
                    headers: headers(),
                    body: JSON.stringify({ message }),
                });
            }
            catch {
                return { success: false, error: 'Failed to send message' };
            }
        },
        /** Save reflection text (draft) */
        async saveReflection(sessionId, reflectionText) {
            try {
                return await request(`${wsBase}/session/${sessionId}/reflection`, {
                    method: 'PATCH',
                    headers: headers(),
                    body: JSON.stringify({ reflectionText }),
                });
            }
            catch {
                return { success: false, error: 'Failed to save reflection' };
            }
        },
        /** Submit the workspace session */
        async submitSession(sessionId, reflectionText) {
            try {
                return await request(`${wsBase}/session/${sessionId}/submit`, {
                    method: 'POST',
                    headers: headers(),
                    body: JSON.stringify({ reflectionText }),
                });
            }
            catch {
                return { success: false, error: 'Failed to submit' };
            }
        },
        /** Delete a workspace session (for reset) */
        async deleteSession(sessionId) {
            try {
                return await request(`${wsBase}/session/${sessionId}`, {
                    method: 'DELETE',
                    headers: headers(),
                });
            }
            catch {
                return { success: false, error: 'Failed to delete session' };
            }
        },
        /** Get full AI prompt config by resource ID (for debug info) */
        async getConfigByResource(resourceId) {
            try {
                return await request(`${wsBase}/config/resource/${resourceId}`, {
                    method: 'GET',
                    headers: headers(),
                });
            }
            catch {
                return { success: false, error: 'Not found' };
            }
        },
    };
}
exports.createAiWorkspaceService = createAiWorkspaceService;
